import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function CroydonCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Croydon Pothole Claims | 43.6% of Residential Roads in RED | Fixtyer"
        description="Croydon Council: 43.6% of unclassified roads in RED condition when last surveyed in 2022, B/C roads last measured three years ago, and ~15,000 potholes filled in five years. What the DfT AMBER rating means for your claim."
        canonical="/councils/croydon"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Croydon', url: '/councils/croydon' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Croydon has a GREEN spend rating — can I still claim for pothole damage?',
            answer: 'Yes. The DfT Spend scorecard is GREEN because Croydon invests well beyond its DfT capital allocation — £12.6m capital spend against a £382k DfT allocation in 2024/25. But your claim turns on whether the specific defect was reasonably inspected and repaired under Section 58, not on aggregate spend. The Condition scorecard is AMBER, and 43.6% of U-roads were in RED condition the last time they were surveyed.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 594.1km — 82% of Croydon\'s 726.9km carriageway network. The council\'s own 2022/23 survey found 43.6% of U-roads in RED (major deterioration) and 39.9% in Amber. B and C roads were last surveyed in 2022 on a three-year cycle, with new surveys planned for summer 2025. If your incident falls in the gap, the council\'s own condition picture for your road type may be years out of date.',
          },
          {
            question: 'Why does the DfT flag Croydon\'s condition data as incomplete?',
            answer: 'The DfT notes that Croydon\'s overall, condition and best-practice scorecards are "based on incomplete road condition data." The council\'s transparency report confirms B, C and U roads were last surveyed in 2022, while A-roads are surveyed annually via LoHEG. That gap in local-road condition reporting is exactly the sort of weakness that undermines a blanket Section 58 defence on residential streets.',
          },
          {
            question: 'Does Croydon\'s 24-hour pothole response target help the council reject my claim?',
            answer: 'It helps them only if they can prove your defect was inspected, classified correctly, and either repaired within 24 hours (for severe potholes) or scheduled within their hierarchy-based timeframe. The council\'s own report states a severe pothole is marked for urgent repair within 24 hours, but also that it still fills around 3,000 potholes per year — evidence that defects continue to form and require reactive repair despite preventative spending at 82%.',
          },
          {
            question: 'Croydon says A-road condition is improving — does that weaken claims on main roads?',
            answer: 'A-roads are a small share of the network — 51.6km of 726.9km total. The council acknowledges principal roads are "improving year on year" but still need "more investment" to hit a long-term target of reducing BPRN roads in RED by 4%. Even on A-roads, your claim still turns on whether the specific defect was known, inspected and treated in time — not on a borough-wide trend line.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Croydon Council"
        councilSlug="croydon"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/croydon"
        headline="Croydon Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Croydon</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Croydon: 43.6% of Residential Roads in RED — Last Measured in 2022
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Croydon earns <strong className="text-emerald-400">GREEN</strong> for spend — investing up to
            {' '}<strong className="text-emerald-400">£13.1m</strong> a year against DfT allocations as low as
            {' '}<strong className="text-emerald-400">£382k</strong>. But the overall DfT rating is{' '}
            <strong className="text-amber-400">AMBER</strong>, partly because B, C and U-road condition data
            is stale: when last surveyed, <strong className="text-rose-400">43.6% of the borough's
            unclassified roads</strong> were in major deterioration — on a network where 594.1km of
            residential streets make up 82% of all carriageways.
          </p>

          {/* Key stat callout */}
          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">43.6%</div>
            <div className="text-white font-medium mt-2">U-roads in RED condition (2022/23 survey)</div>
            <div className="text-brand-300 text-sm mt-1">
              Plus 39.9% Amber on 594.1km of residential and minor roads — surveyed on a three-year
              cycle, with the next survey due in summer 2025.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network overview */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            A 727km Network — Mostly Residential
          </h2>
          <p className="text-surface-600 mb-8">
            Croydon's own highway inventory from the June 2025 transparency report
          </p>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-brand-900">726.9 km</div>
              <div className="text-sm text-surface-600 mt-1">Total carriageways</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-brand-900">51.6 km</div>
              <div className="text-sm text-surface-600 mt-1">A-roads (7.1%)</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-brand-900">81.2 km</div>
              <div className="text-sm text-surface-600 mt-1">B/C roads (11.2%)</div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-rose-600">594.1 km</div>
              <div className="text-sm text-rose-800 mt-1">U-roads (81.7%)</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Section 41 — The Duty</h3>
              <p className="text-surface-600 text-sm">
                Croydon states it has "a legal requirement to maintain public highways under Section 41
                of the Highways Act 1980." A-roads carry traffic to Brighton and the South-East; B and C
                roads link those routes to the residential network that makes up most of the borough.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">DfT Incomplete Data Flag</h3>
              <p className="text-amber-800 text-sm">
                The DfT explicitly notes Croydon's overall, condition and best-practice scorecards are
                "based on incomplete road condition data." That is not our characterisation — it is the
                government's own caveat on this borough's ratings.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            A-roads surveyed annually via LoHEG; B, C and U roads last measured in 2022 on a three-year cycle
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (51.6km — 7.1% of network): improving, council says</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">49.0%</td>
                    <td className="px-4 py-2 text-center">45.3%</td>
                    <td className="px-4 py-2 text-center">38.2%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">26.4%</td>
                    <td className="px-4 py-2 text-center">26.1%</td>
                    <td className="px-4 py-2 text-center">21.8%</td>
                  </tr>
                  <tr className="bg-brand-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">24.6%</td>
                    <td className="px-4 py-2 text-center font-bold">28.6%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">40.0%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm mb-4">
              Principal roads are surveyed yearly through AI-driven LoHEG/BPRN surveys. The council reports
              the overall condition is "improving year on year," with a long-term target to reduce BPRN roads
              in RED by 4%.
            </p>
            <QuoteCard
              quote="The overall condition is improving year on year, but more investment is needed to achieve our long-term target as set out in our Highways Asset Management Strategy which details a target reduction of 4% of the borough principal road network (BPRN) in major deterioration (red) – longer term."
              source="Croydon Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (81.2km — 11.2% of network): 2022/23 snapshot only</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">18.0%</div>
                <div className="text-sm text-rose-800">RED</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">24.1%</div>
                <div className="text-sm text-amber-800">Amber</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">42.1%</div>
                <div className="text-sm text-emerald-800">Green</div>
              </div>
            </div>
            <p className="text-amber-800 text-sm">
              B and C roads were last surveyed in 2022, on a three-year frequency. No updated condition
              figures appear in the 2025 report — surveys were planned for summer 2025.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-rose-900 mb-4">U-roads (594.1km — 81.7% of network): the claim hotspot</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">43.6%</div>
                <div className="text-sm text-rose-800">RED</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">39.9%</div>
                <div className="text-sm text-amber-800">Amber</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">32.3%</div>
                <div className="text-sm text-emerald-800">Green</div>
              </div>
            </div>
            <p className="text-rose-800 font-medium">
              When last measured in 2022/23, more than four in ten Croydon residential roads were in RED
              condition — major deterioration requiring maintenance consideration. That figure is three
              years old.
            </p>
          </div>

          <QuoteCard
            quote="Classified non-principal (B&C) and unclassified (U) roads have been last surveyed in 2022, with a frequency of 3 years. The data was collected via a driven-conditioned (artificial intelligence) survey, and road condition categorised using a slightly different AI model to that used on the borough principal road network (BPRN)."
            source="Croydon Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* The survey gap */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Three-Year Local-Road Blind Spot
          </h2>
          <p className="text-surface-600 mb-8">
            82% of Croydon's carriageways run on a survey cycle the 2025 report has not yet updated
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Surveys are being carried out in the summer of 2025, which will give decision-makers a better picture of the evolution of road condition since the last survey, and how the treatments undertaken have performed against the expected network deterioration."
              source="Croydon Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Inspection vs Condition Survey</h3>
              <p className="text-surface-600 text-sm mb-3">
                Croydon assigns every road a network hierarchy that determines safety inspection frequency
                — busier roads inspected more often, quieter streets less so. But condition surveys for B, C
                and U roads run every three years, using a different AI model to the principal road network.
              </p>
              <p className="text-surface-600 text-sm">
                A safety inspection regime is not the same as knowing network-wide condition. For Section 58,
                ask whether the council had reasonable knowledge of deterioration on your specific road —
                not just whether an inspector drove past on schedule.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Response Time Targets</h3>
              <p className="text-amber-800 text-sm mb-3">
                Reactive jobs are raised through a digital asset management system with priority levels by
                defect severity. The council states:
              </p>
              <p className="text-amber-800 text-sm italic">
                "a severe pothole is marked for urgent works to be repaired within 24 hours, whereas a fine
                crack appearing on the road surface could be marked for repair within the next month."
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, Croydon must show a reasonable system for knowing and
              fixing defects. For the borough's U-road network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — and was that before or after the 2022 snapshot?</li>
              <li>• If 43.6% of U-roads were RED at the last survey, what was done about yours specifically?</li>
              <li>• Does a three-year survey cycle provide reasonable knowledge on a network filling ~3,000 potholes a year?</li>
              <li>• Can the council prove your defect was inspected, classified and repaired within its own response targets?</li>
            </ul>
            <p className="text-white font-medium">
              Stale condition data on 594.1km of residential roads is not a strong foundation for rejecting
              every pothole claim with a generic "adequate system" letter.
            </p>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            GREEN Spend — But Patching Over Localised Failures
          </h2>
          <p className="text-surface-600 mb-8">
            Croydon invests heavily in highways — the DfT Spend scorecard reflects that. The question is
            whether it matches where defects actually form.
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital (£k)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend (£k)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue (£k)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Reactive</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Resurfaced (km)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center text-surface-400">—</td>
                  <td className="px-4 py-3 text-center">6,693</td>
                  <td className="px-4 py-3 text-center">266</td>
                  <td className="px-4 py-3 text-center">73%</td>
                  <td className="px-4 py-3 text-center">27%</td>
                  <td className="px-4 py-3 text-center">11.7</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center text-surface-400">—</td>
                  <td className="px-4 py-3 text-center">10,077</td>
                  <td className="px-4 py-3 text-center">338</td>
                  <td className="px-4 py-3 text-center">73%</td>
                  <td className="px-4 py-3 text-center">27%</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">45.2</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center text-surface-400">—</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">13,150</td>
                  <td className="px-4 py-3 text-center">692</td>
                  <td className="px-4 py-3 text-center">80%</td>
                  <td className="px-4 py-3 text-center">20%</td>
                  <td className="px-4 py-3 text-center">32.6</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">382</td>
                  <td className="px-4 py-3 text-center">9,940</td>
                  <td className="px-4 py-3 text-center">1,184</td>
                  <td className="px-4 py-3 text-center">73%</td>
                  <td className="px-4 py-3 text-center">27%</td>
                  <td className="px-4 py-3 text-center">15.7</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">382</td>
                  <td className="px-4 py-3 text-center">12,555</td>
                  <td className="px-4 py-3 text-center">1,747</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">82%</td>
                  <td className="px-4 py-3 text-center">18%</td>
                  <td className="px-4 py-3 text-center">15.8</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">1,241</td>
                  <td className="px-4 py-3 text-center">11,500</td>
                  <td className="px-4 py-3 text-center">949</td>
                  <td className="px-4 py-3 text-center">81%</td>
                  <td className="px-4 py-3 text-center">19%</td>
                  <td className="px-4 py-3 text-center">24.5</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="For the past few years, our budget allocated for highways maintenance has varied over the last five years (up to £13.1M), with a varying type of treatment being undertaken over the last year, especially, to allow the budget to cover more kilometres of the road network. And as such, more partial resurfacing and patching programmes were carried out to address localised issues."
              source="Croydon Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">The Well-Funded, Still-Amber Paradox</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£382k</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2024/25</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£12.6m</div>
                <div className="text-brand-300 text-sm">Actual capital spend 2024/25</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">82%</div>
                <div className="text-brand-300 text-sm">Preventative share 2024/25</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Croydon spends roughly <span className="text-emerald-400">33 times its DfT capital allocation</span>
              {' '}from its own capital programme — and still carries an AMBER condition rating with stale
              local-road survey data. Money is not the whole story; knowing where defects are forming is.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            ~15,000 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volume tells you how many defects this network still produces
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled (estimate)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">3,113</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">2,939</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">3,718</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">3,140</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total (report estimate)</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">~15,000</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="We aim to keep our network safe by reacting as fast as possible to potholes and other defects endangering the public and their properties. In the last 5 years, we have filled around 15,000 potholes, with an average of 3,000 potholes per year as seen in Table 2. We aim to reduce this number by being more proactive and increasing our planned maintenance works, to avoid expensive reactive maintenance. We expect to fill in around 2,000-3,000 potholes this year, showing a reduction on average to previous years observed."
              source="Croydon Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~8 Potholes Every Day</h3>
              <p className="text-surface-600 text-sm">
                At roughly 3,000 pothole repairs per year, Croydon fills around eight defects every day —
                on a network where 43.6% of U-roads were already in RED condition at the last survey. That
                is a network producing defects faster than three-year condition monitoring can track.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">2025/26 Forward Plan</h3>
              <p className="text-amber-800 text-sm">
                The council plans 71 roads for planned maintenance in 2025/26, around 20km of carriageway
                resurfacing and significant crack sealing — techniques ranging from crack sealing on
                Addington Road to 40mm resurfacing on Grant Road. That is proactive work, but it covers a
                fraction of 726.9km.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Forward plans & methodology */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            How Croydon Prioritises Repairs
          </h2>
          <p className="text-surface-600 mb-8">
            Asset management strategy, inspection hierarchy, and the 2025/26 works programme
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="We plan maintenance works using a priority list that takes into account the condition of the road, its hierarchy, the reactive jobs recently completed, and any public claims or enquiries. The list is then refined considering local context and efficiencies to create a practical and balanced works programme."
              source="Croydon Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey Methodology</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• A-roads: annual LoHEG AI-driven condition surveys (BPRN)</li>
                <li>• B/C and U roads: AI-driven surveys every three years (last: 2022)</li>
                <li>• Footways: driven video AI survey every three years (PAS 2161 from 2026/27)</li>
                <li>• RAG categories: Green (no treatment), Amber (maintenance soon), Red (maintenance should be considered)</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Asset Value Context (Annex B)</h3>
              <p className="text-surface-600 text-sm mb-3">
                Croydon's indicative gross replacement cost for the entire highway network at end of
                2024/25 is £2.16bn, with carriageways alone at £1.18bn. Maintenance spend represented
                0.58% of asset value in 2024/25.
              </p>
              <p className="text-surface-600 text-sm">
                The council updated its Highways Asset Management Strategy in 2024 and tracks performance
                through nine contractor KPIs including emergency callout response times.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Public Claims Feed The Priority List</h3>
            <p className="text-brand-200 mb-4">
              Croydon explicitly includes "public claims or enquiries" in its maintenance prioritisation.
              That cuts both ways:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• A prior report on FixMyStreet or Love Clean Streets may prove the council had notice before your incident</li>
              <li>• If your claim was logged but the road was not treated, that weakens any "unknown defect" argument</li>
              <li>• The council engages residents, ward councillors and community groups when setting priorities — documented complaints matter</li>
            </ul>
            <p className="text-white font-medium">
              Specificity wins: date-stamped photos, prior reports, and the road's place in Croydon's
              hierarchy are more persuasive than arguing the council "doesn't spend enough."
            </p>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a GREEN-Spend AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Croydon is not a spend-starved authority — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend — capital investment up to £13.1m, far exceeding DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 73–82% preventative spending across the five-year table
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> AMBER best practice — documented HAMS, contractor KPIs, ISO-aligned processes via FM Conway
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road condition reported as improving year on year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 24-hour target for severe potholes — a concrete response standard they can point to
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence citing spend levels and inspection regimes. Generic "the roads are terrible" claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — DfT flags incomplete local-road condition data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 43.6% of U-roads in RED at the last 2022/23 survey on 594.1km of residential network
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> B/C/U roads not re-surveyed since 2022 — three-year gap before summer 2025 programme
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> ~15,000 potholes filled in five years — defects still forming despite heavy spend
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits "more partial resurfacing and patching" to stretch budget over more kilometres
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and documented asset management, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, Love Clean Streets, council enquiries) — proof of actual notice</li>
              <li>• Photos showing defect size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, the 2022 survey data and three-year cycle gap are your strongest structural arguments</li>
              <li>• Whether the council met its own 24-hour severe-pothole target for your location</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Croydon's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Croydon?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road 43.6% RED argument</li>
                <li>• No prior-report search</li>
                <li>• No three-year survey-gap analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 43.6% U-road RED condition cited from council data</li>
                <li>✅ Three-year local-road survey gap argued</li>
                <li>✅ ~15,000 potholes in five years contextualised</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Croydon</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=croydon"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Croydon Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Croydon has a GREEN spend rating — can I still claim for pothole damage?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN because Croydon invests well beyond its DfT capital
                allocation — £12.6m capital spend against a £382k DfT allocation in 2024/25. But your claim
                turns on whether the specific defect was reasonably inspected and repaired under Section 58,
                not on aggregate spend. The Condition scorecard is AMBER, and 43.6% of U-roads were in RED
                condition the last time they were surveyed.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 594.1km — 82% of Croydon's 726.9km carriageway network. The council's own
                2022/23 survey found 43.6% of U-roads in RED (major deterioration) and 39.9% in Amber. B and
                C roads were last surveyed in 2022 on a three-year cycle, with new surveys planned for summer
                2025. If your incident falls in the gap, the council's own condition picture for your road type
                may be years out of date.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why does the DfT flag Croydon's condition data as incomplete?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT notes that Croydon's overall, condition and best-practice scorecards are "based on
                incomplete road condition data." The council's transparency report confirms B, C and U roads
                were last surveyed in 2022, while A-roads are surveyed annually via LoHEG. That gap in
                local-road condition reporting is exactly the sort of weakness that undermines a blanket
                Section 58 defence on residential streets.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Croydon's 24-hour pothole response target help the council reject my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It helps them only if they can prove your defect was inspected, classified correctly, and either
                repaired within 24 hours (for severe potholes) or scheduled within their hierarchy-based
                timeframe. The council's own report states a severe pothole is marked for urgent repair within
                24 hours, but also that it still fills around 3,000 potholes per year — evidence that defects
                continue to form and require reactive repair despite preventative spending at 82%.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Croydon says A-road condition is improving — does that weaken claims on main roads?
              </h3>
              <p className="text-surface-600 text-sm">
                A-roads are a small share of the network — 51.6km of 726.9km total. The council acknowledges
                principal roads are "improving year on year" but still need "more investment" to hit a long-term
                target of reducing BPRN roads in RED by 4%. Even on A-roads, your claim still turns on whether
                the specific defect was known, inspected and treated in time — not on a borough-wide trend line.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.croydon.gov.uk/streets-roads-and-transport/local-highways-maintenance-transparency-reports"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Croydon Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
